import { Box } from '@mui/material';
import extractionFileMatchesForSend, {
  getExtractionRoutingDescriptor,
} from 'components/@home/@documents/SentDocuments/extractionFileMatch';
import { SendDrawerContext } from 'components/@home/@documents/SentDocuments/SendDrawerContext';
import DropzoneBox from 'components/common/DropzoneBox';
import Loading from 'components/common/Loading';
import { throttle } from 'lodash';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import { array, bool, func, number, object } from 'prop-types';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { I18n } from 'utils/i18n';
import { bindActionCreators } from 'redux';
import bulkMessagings from 'store/app/entities/bulkMessaging/action';
import getPdfjs from 'utils/pdfjs';
import DroppedFiles from './DroppedFiles';
import { getFileName, getParts } from './pdf-utils';

const readFile = file => {
  return new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = e => resolve(e.target.result);
    reader.readAsArrayBuffer(file);
  });
};

const readFileAsString = file => {
  return new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = e => resolve(e.target.result);
    reader.readAsText(file, 'utf8');
  });
};

const StepFiles = ({
  setError,
  parsingPct,
  setParsingPct,
  nextEnabled,
  setNextEnabled,
  resets,
  userItems,
  files,
  setFiles,
  hideFindColumn = false,
  format: formatProp = null,
}) => {
  const { file: formatCtx } = useContext(SendDrawerContext);
  const format = formatProp || formatCtx;
  const [droppedFiles, setDroppedFiles] = useState({});
  const dispatch = useDispatch();
  const actions = {
    bulkMessagings: bindActionCreators(bulkMessagings, dispatch),
  };

  const [loading, setLoading] = useState(false);
  const parseFailed = useRef(false);
  const parseFailedAt = useRef(0);

  /** When routing boxes change after the first parse, re-run extraction without re-dropping files. */
  const routingExtractionKey = useMemo(() => {
    const items = format?.items || [];
    return items
      .filter(i => i.selector === 'box' && i.coords && i.coords.left != null)
      .map(i =>
        [
          i.name,
          i.page || 1,
          i.position ?? '',
          i.join ?? '',
          i.coords.left,
          i.coords.top,
          i.coords.right,
          i.coords.bottom,
        ].join(':'),
      )
      .sort()
      .join('|');
  }, [format?.items]);

  /**
   * Only rows that are still "pending" parent seeds (`{ file }` before `getParts` writes `pageNum`).
   * Including every parsed row made the fingerprint identical forever (same File refs), so any effect
   * dependency twitch re-ran parsing in a loop.
   */
  const parentStubFilesFingerprint = useMemo(() => {
    const seen = new Set();
    const keys = [];
    for (const row of files || []) {
      const file = row?.file;
      if (!file || typeof file.name !== 'string') continue;
      if (row.pageNum != null) continue;
      const k = `${file.name}\0${file.size}\0${file.lastModified}`;
      if (!seen.has(k)) {
        seen.add(k);
        keys.push(k);
      }
    }
    return keys.sort().join('|');
  }, [files]);

  const handleDrop = newDroppedFiles => {
    setDroppedFiles(newDroppedFiles.reduce((acc, f) => ({ ...acc, [f.name]: f }), {}));
  };

  const parseFiles = debounce(
    useCallback(() => {
      if (!format) {
        return;
      }
      const fromDropzone = Object.values(droppedFiles).filter(Boolean);
      const fromParentRows = (files || []).map(f => f.file).filter(Boolean);
      const newFiles = !isEmpty(fromDropzone)
        ? fromDropzone
        : Array.from(new Map(fromParentRows.map(f => [f, f])).keys());
      if (isEmpty(newFiles)) {
        return;
      }
      parseFailed.current = false;
      setLoading(true);
      const initResets = resets.current;
      const parsed = newFiles.map(() => 0);
      /** First parsed row replaces seeded rows / clears list; later rows append. */
      let clearListOnce = true;
      setError(null);
      Promise.all(
        newFiles
          .filter(f => format.type !== 'xml' || f?.name.toLowerCase().endsWith('.xml'))
          .map(async (file, idx) => {
            if (resets.current !== initResets) {
              setParsingPct(0);
              return [];
            }
            let xml;
            let pdf;
            let pdfLoadingTask = null;
            if (format.type === 'xml') {
              xml = await readFileAsString(file);
            } else {
              const arrayBuffer = await readFile(file);
              const fileContent = new Uint8Array(arrayBuffer);
              const pdfjs = await getPdfjs();
              pdfLoadingTask = pdfjs.getDocument({ data: fileContent });
              pdf = await pdfLoadingTask.promise;
            }
            const routeSets = { code: new Set(), id: new Set() };
            const fileMap = { xml, 'single-pdf': pdf, 'multi-pdf': pdf };
            await getParts(file.name, fileMap[format.type], format, async (parts, pageIdx) => {
              const sendByField = format.sendBy || 'code';
              const { field, routedByDni, routeCheckFormat, routeCheckKey } =
                getExtractionRoutingDescriptor(sendByField, parts);
              if (field === 'id' && Object.keys(parts).includes('code')) {
                delete parts.code;
              }
              if (routeCheckKey) {
                routeSets[routeCheckFormat].add(routeCheckKey);
              }
              const rowHasEmptyExtractedField = Object.entries(parts)
                .filter(([key]) => {
                  if (key === 'code' && field === 'id') return false;
                  if (key === 'code' && routedByDni) return false;
                  return true;
                })
                .some(([, value]) => value === '');
              const error = rowHasEmptyExtractedField || !routeCheckKey;
              if (resets.current !== initResets) {
                setParsingPct(0);
                return;
              }
              await setFiles(oldValue => {
                const base = clearListOnce ? [] : oldValue || [];
                clearListOnce = false;
                return [
                  ...base,
                  {
                    ...parts,
                    file: droppedFiles[parts.filename] || file,
                    xml,
                    pageNum: pageIdx,
                    newFileName: getFileName({ ...parts, ...userItems }, format),
                    error,
                    routedByDni,
                    routeCheckFormat,
                    routeCheckKey,
                    employee: {},
                  },
                ];
              });
              const total = format?.type === 'single-pdf' ? pdf.numPages : 1;
              parsed[idx] = Math.max(parsed[idx], ((pageIdx + 1) / total) * 100);
              setParsingPct(
                Math.round(parsed.reduce((acc, curr) => acc + curr, 0) / parsed.length),
              );
            });
            pdfLoadingTask?.destroy();
            pdfLoadingTask?._worker.destroy();
            return routeSets;
          }),
      )
        .then(setsArr => {
          const merged = { code: new Set(), id: new Set() };
          for (const s of setsArr) {
            s.code.forEach(x => merged.code.add(x));
            s.id.forEach(x => merged.id.add(x));
          }
          return Promise.all([
            merged.code.size
              ? actions.bulkMessagings.checkIds({ ids: [...merged.code], format: 'code' })
              : Promise.resolve({ value: { existingEmployees: {} } }),
            merged.id.size
              ? actions.bulkMessagings.checkIds({ ids: [...merged.id], format: 'id' })
              : Promise.resolve({ value: { existingEmployees: {} } }),
          ]).then(([codeRes, idRes]) => ({
            existingEmployeesCode: codeRes.value?.existingEmployees || {},
            existingEmployeesId: idRes.value?.existingEmployees || {},
          }));
        })
        .then(({ existingEmployeesCode, existingEmployeesId }) => {
          const hasCodeMatches = Object.keys(existingEmployeesCode || {}).length > 0;
          const hasIdMatches = Object.keys(existingEmployeesId || {}).length > 0;
          if (hasCodeMatches || hasIdMatches) {
            setFiles(oldValue =>
              oldValue.map(f => {
                const map =
                  f.routeCheckFormat === 'id' ? existingEmployeesId : existingEmployeesCode;
                const existingEmployee = map[f.routeCheckKey];
                return {
                  ...f,
                  employee: { ...f.employee, ...existingEmployee },
                };
              }),
            );
          }
          setLoading(false);
        })
        .catch(() => {
          if (resets.current !== initResets) return;
          parseFailed.current = true;
          parseFailedAt.current = resets.current;
          setError(I18n.t('HrBot.Error parsing some files'));
          setParsingPct(0);
          setLoading(false);
        });
    }, [
      actions.bulkMessagings,
      droppedFiles,
      files,
      format,
      resets,
      setError,
      setFiles,
      setParsingPct,
      userItems,
    ]),
    300,
  );

  useEffect(() => {
    if (nextEnabled && parsingPct < 100) {
      setNextEnabled(false);
    } else if (!nextEnabled && parsingPct >= 100) {
      setNextEnabled(true);
    }
  }, [nextEnabled, parsingPct, setNextEnabled]);

  const [parseFilesWhenFinished, setParseFilesWhenFinished] = useState(false);
  const parseFilesRef = useRef(parseFiles);
  parseFilesRef.current = parseFiles;
  /** Must not be an effect dep: `setParsingPct` fires per PDF page and would retrigger parse in a loop. */
  const parsingPctRef = useRef(parsingPct);
  parsingPctRef.current = parsingPct;

  useEffect(() => {
    if ((!parsingPct || parsingPct >= 100) && parseFilesWhenFinished) {
      setParseFilesWhenFinished(false);
      parseFilesRef.current();
    }
  }, [parseFilesWhenFinished, parsingPct]);

  useEffect(() => {
    const hasDropzoneFiles = !isEmpty(droppedFiles);
    const hasParentStubs = parentStubFilesFingerprint.length > 0;
    if (!hasDropzoneFiles && !hasParentStubs) return;
    const pct = parsingPctRef.current;
    if (pct && pct < 100) {
      setParseFilesWhenFinished(true);
    } else {
      parseFilesRef.current();
    }
  }, [routingExtractionKey, droppedFiles, parentStubFilesFingerprint]);

  /** Refresh derived filenames when routing defaults (month/year/day) change — without re-running PDF extraction. */
  useEffect(() => {
    if (!format) return;
    setFiles(prev => {
      if (!prev?.some(r => r.pageNum != null)) return prev;
      let changed = false;
      const next = prev.map(row => {
        if (row.pageNum == null) return row;
        const nextName = getFileName({ ...row, ...userItems }, format);
        if (nextName === row.newFileName) return row;
        changed = true;
        return { ...row, newFileName: nextName };
      });
      return changed ? next : prev;
    });
  }, [userItems, format, setFiles]);

  const setErrors = useMemo(
    () =>
      throttle(() => {
        if (parseFailed.current && resets.current !== parseFailedAt.current) {
          parseFailed.current = false;
        }
        if (parseFailed.current) {
          setError(I18n.t('HrBot.Error parsing some files'));
          setNextEnabled(false);
          return;
        }
        const hasErrors = files?.filter(f => f.error).length > 0;
        const sendByField = format?.sendBy || 'code';
        const hasRecipients =
          files?.filter(f => extractionFileMatchesForSend(f, sendByField)).length > 0;
        if (hasErrors) {
          setError(I18n.t('HrBot.Error parsing some files'));
        } else if (files?.length && !hasRecipients) {
          setError(I18n.t('HrBot.No recipients found'));
        } else {
          setError(null);
        }
        setNextEnabled(!hasErrors && hasRecipients);
      }, 300),
    [files, format, resets, setError, setNextEnabled],
  );
  useEffect(() => {
    setErrors();
  }, [setErrors]);

  const accept = {
    pdf: 'application/pdf',
    xml: 'application/pdf, application/xml, text/xml',
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        mx: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          maxHeight: '100%',
          overflow: 'hidden',
        }}
      >
        {loading && (
          <Loading
            sx={{
              flexGrow: 1,
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            debounce={20}
            size={64}
          />
        )}
        {!loading &&
          format &&
          (files ? (
            <DroppedFiles files={files} hideFindColumn={hideFindColumn} />
          ) : (
            <DropzoneBox
              onDrop={handleDrop}
              accept={accept[format.type] || 'application/pdf'}
              multiple
            />
          ))}
      </Box>
    </Box>
  );
};

StepFiles.propTypes = {
  resets: object.isRequired,
  setError: func.isRequired,
  nextEnabled: bool.isRequired,
  setNextEnabled: func.isRequired,
  parsingPct: number.isRequired,
  setParsingPct: func.isRequired,
  userItems: object.isRequired,
  format: object,
  files: array.isRequired,
  setFiles: func.isRequired,
  hideFindColumn: bool,
};

export default StepFiles;
